import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Lock, User, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isSetup, setIsSetup] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    email: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      navigate("/admin/dashboard");
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: formData.username,
        password: formData.password
      });

      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.user));
      toast.success("Signed in successfully");
      navigate("/admin/dashboard");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid credentials");
      } else {
        toast.error("Sign in failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/setup`, {
        username: formData.username,
        password: formData.password,
        email: formData.email
      });

      toast.success("Account created. Please sign in.");
      setIsSetup(false);
    } catch (error) {
      if (error.response?.data?.detail?.includes("already exists")) {
        toast.error("Account exists. Please sign in.");
        setIsSetup(false);
      } else {
        toast.error("Setup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#18181B] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-xs font-medium tracking-[0.2em] text-[#71717A] mb-2">
            STEWARD LOTS™
          </p>
          <h1 className="text-xl font-semibold text-white">Steward Console</h1>
          <p className="text-sm text-[#71717A] mt-2">
            Authorized access only
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-sm p-6">
          <form onSubmit={isSetup ? handleSetup : handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-xs font-medium text-[#52525B] uppercase tracking-wide">
                Username
              </Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <Input
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className="pl-10 h-10 border-[#E4E4E7] focus:border-[#18181B] focus:ring-[#18181B]"
                  placeholder="Enter username"
                  required
                  data-testid="input-username"
                />
              </div>
            </div>

            {isSetup && (
              <div>
                <Label htmlFor="email" className="text-xs font-medium text-[#52525B] uppercase tracking-wide">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1.5 h-10 border-[#E4E4E7] focus:border-[#18181B] focus:ring-[#18181B]"
                  placeholder="Enter email"
                  required
                  data-testid="input-email"
                />
              </div>
            )}

            <div>
              <Label htmlFor="password" className="text-xs font-medium text-[#52525B] uppercase tracking-wide">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 h-10 border-[#E4E4E7] focus:border-[#18181B] focus:ring-[#18181B]"
                  placeholder="Enter password"
                  required
                  data-testid="input-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-10 bg-[#18181B] hover:bg-[#27272A] text-white font-medium"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "Please wait..." : (isSetup ? "Create Account" : "Sign In")}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>

          <div className="mt-4 pt-4 border-t border-[#E4E4E7] text-center">
            <button
              onClick={() => setIsSetup(!isSetup)}
              className="text-xs text-[#71717A] hover:text-[#18181B] transition-colors"
              data-testid="toggle-setup-btn"
            >
              {isSetup ? "Have an account? Sign in" : "First time? Create account"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[#52525B] text-xs mt-6">
          Stewardship · Accountability · Order
        </p>
      </div>
    </div>
  );
}
